import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiProblem } from '../api/problem.js';
import { ApiErrorView } from '../components/api-error-view.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/card.js';
import { PageHeader } from '../components/page-header.js';
import { formatCivilDate, formatHours, STATUS_LABELS } from '../requests/format.js';
import { Pagination, PastLastPage, pageFrom, StatusFilter, TABLE, TD, TH, useListFilters, usePageHref, useRequestQueryFilters } from '../requests/request-list.js';
import { RequestSearchForm } from '../requests/request-search.js';
import { useScopedRequestsPage } from '../requests/requests-api.js';
import { StatusBadge } from '../requests/status-badge.js';
import { useMyTeamPage } from '../team/team-api.js';
import { TeamCalendar, useTeamCalendar } from '../team/team-calendar.js';

/** Un 403 de cualquiera de las tres lecturas: el usuario no es Responsable; se muestra una sola vez. */
const forbidden = (error: unknown) => error instanceof ApiProblem && error.status === 403;

/**
 * Mi equipo (W5): el equipo directo (`GET /me/team`) y sus solicitudes (`GET /requests?scope=team`). Cada
 * lista tiene su página en la URL (`?equipo=` y `?pagina=`, con `?estado=` y los filtros de H-02 para las
 * solicitudes), así que paginar una no borra la otra. Entre el equipo y la lista, el calendario del equipo, con
 * su propia lectura del mes visible (fuera de la URL y sin los filtros de la lista). El ámbito lo decide la API.
 * Con el aspecto de Mis solicitudes: cabecera, cada lista en su tarjeta y los filtros en otra.
 */
export function TeamPage() {
  const [params] = useSearchParams();
  const teamPage = pageFrom(params, 'equipo');
  const [filters, setStatus] = useListFilters();
  const [search, applySearch] = useRequestQueryFilters();
  const team = useMyTeamPage(teamPage);
  const requests = useScopedRequestsPage('team', { ...search, areaId: '', status: filters.status, page: filters.page });
  const calendar = useTeamCalendar();

  const denied = [team.error, calendar.result.error, requests.error].find(forbidden);
  if (denied) return <ApiErrorView error={denied} />;

  return (
    <div className="space-y-7 lg:px-6 lg:py-4">
      <PageHeader title="Mi equipo" description="Solicitudes de tus subordinados directos, sin incluir niveles indirectos." />
      <TeamMembers page={teamPage} result={team} />
      <TeamCalendar state={calendar} />
      <TeamRequests filters={filters} setStatus={setStatus} search={search} applySearch={applySearch} result={requests} />
    </div>
  );
}

/** Cabecera y filas de las dos tablas, como en Mis solicitudes. */
const TABLE_HEAD = 'border-b border-border bg-muted text-muted-foreground';
const ROW = 'border-t border-border transition-colors first:border-t-0 hover:bg-muted';
const LINK = 'text-primary underline-offset-4 hover:underline';

/** Tarjeta de una lista: su título arriba y debajo la carga, el error, el vacío o la tabla. */
function ListCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card flush>
      <CardHeader className="border-b border-border py-5">
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      {children}
    </Card>
  );
}

/** Texto de una lista sin filas (vacía, cargando o página más allá del final), dentro de su tarjeta. */
const EMPTY = 'px-5 py-16 text-center text-sm text-muted-foreground';

function TeamMembers({ page, result }: { page: number; result: ReturnType<typeof useMyTeamPage> }) {
  const pageHref = usePageHref('equipo');
  return (
    <div>
      <ListCard title="Equipo">
        {result.isPending ? <output className="block p-5 text-muted-foreground">Cargando el equipo…</output> : null}
        {result.isError ? (
          <div className="p-5">
            <ApiErrorView error={result.error} />
          </div>
        ) : null}
        {result.isSuccess && result.data.total === 0 ? <p className={EMPTY}>No tienes personas a cargo.</p> : null}
        {result.isSuccess && result.data.total > 0 && result.data.items.length === 0 ? (
          <div className="p-5">
            <PastLastPage firstPage={pageHref(1)} what="personas" />
          </div>
        ) : null}
        {result.isSuccess && result.data.items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className={TABLE} aria-label="Equipo">
              <thead className={TABLE_HEAD}>
                <tr>
                  <th scope="col" className={TH}>Nombre</th>
                  <th scope="col" className={TH}>Correo</th>
                  <th scope="col" className={TH}>Cargo</th>
                  <th scope="col" className={TH}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {result.data.items.map((member) => (
                  <tr key={member.id} className={ROW}>
                    <th scope="row" className={TH}>
                      <Link to={`/mi-equipo/${member.id}`} className={LINK}>
                        {member.fullName}
                      </Link>
                    </th>
                    <td className={TD}>{member.email}</td>
                    <td className={TD}>{member.jobTitle ?? '—'}</td>
                    <td className={TD}>{member.active ? 'Activo' : 'Inactivo'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </ListCard>
      {result.isSuccess && result.data.total > 0 ? (
        <Pagination
          page={page}
          total={result.data.total}
          pageSize={result.data.pageSize}
          hrefFor={pageHref}
          label="Paginación del equipo"
          units={['persona', 'personas']}
        />
      ) : null}
    </div>
  );
}

function TeamRequests({
  filters,
  setStatus,
  search,
  applySearch,
  result,
}: {
  filters: ReturnType<typeof useListFilters>[0];
  setStatus: ReturnType<typeof useListFilters>[1];
  search: ReturnType<typeof useRequestQueryFilters>[0];
  applySearch: ReturnType<typeof useRequestQueryFilters>[1];
  result: ReturnType<typeof useScopedRequestsPage>;
}) {
  const pageHref = usePageHref('pagina');
  const { status } = filters;
  return (
    <div className="space-y-7">
      <Card>
        <CardContent className="space-y-4">
          <StatusFilter status={status} onChange={setStatus} className="" />
          <RequestSearchForm key={Object.values(search).join('\u0000')} filters={search} onApply={applySearch} showArea={false} className="" />
        </CardContent>
      </Card>
      <div>
        <ListCard title="Solicitudes del equipo">
          {result.isPending ? <output className="block p-5 text-muted-foreground">Cargando las solicitudes…</output> : null}
          {result.isError ? (
            <div className="p-5">
              <ApiErrorView error={result.error} />
            </div>
          ) : null}
          {result.isSuccess && result.data.total === 0 ? (
            <p className={EMPTY}>
              {status === undefined ? 'Tu equipo no tiene solicitudes.' : `Tu equipo no tiene solicitudes en estado «${STATUS_LABELS[status]}».`}
            </p>
          ) : null}
          {result.isSuccess && result.data.total > 0 && result.data.items.length === 0 ? (
            <div className="p-5">
              <PastLastPage firstPage={pageHref(1)} />
            </div>
          ) : null}
          {result.isSuccess && result.data.items.length > 0 ? (
            <div className="overflow-x-auto">
              <table className={TABLE} aria-label="Solicitudes del equipo">
                <thead className={TABLE_HEAD}>
                  <tr>
                    <th scope="col" className={TH}>Solicitud</th>
                    <th scope="col" className={TH}>Solicitante</th>
                    <th scope="col" className={TH}>Tipo de permiso</th>
                    <th scope="col" className={TH}>Fecha del permiso</th>
                    <th scope="col" className={TH}>Horario</th>
                    <th scope="col" className={TH}>Horas solicitadas</th>
                    <th scope="col" className={TH}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {result.data.items.map((request) => (
                    <tr key={request.id} className={ROW}>
                      <th scope="row" className={TH}>
                        <Link to={`/solicitudes/${request.id}`} className={LINK}>
                          {request.requestNumber}
                        </Link>
                      </th>
                      <td className={TD}>{request.employee.fullName}</td>
                      <td className={TD}>{request.permissionType.name}</td>
                      <td className={TD}>{formatCivilDate(request.permissionDate)}</td>
                      <td className={TD}>
                        {request.departureTime}–{request.returnTime}
                      </td>
                      <td className={TD}>{formatHours(request.requestedHours)}</td>
                      <td className={TD}>
                        <StatusBadge status={request.status} tokens />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </ListCard>
        {result.isSuccess && result.data.total > 0 ? (
          <Pagination page={filters.page} total={result.data.total} pageSize={result.data.pageSize} hrefFor={pageHref} label="Paginación de solicitudes" />
        ) : null}
      </div>
    </div>
  );
}
